import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import axios, { AxiosError } from 'axios';
import { AuthMode } from 'src/contexts/AuthContext';
import { updateLocalStorageUserID } from 'src/utils/usersUtils';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { useAuth } from 'src/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

export interface JoinFamilyRequest {
  join_code: string;
  user_id: number;
}

export interface JoinFamilyResponse {
  firebase_id: string;
  custom_token: string;
}

/**
 * Mutation hook to join a family using a family code and user ID
 * This hook is used when a child selects their profile from a family
 * @returns A mutation result object with methods to join a family
 */
export const useJoinFamily = (): UseMutationResult<
  JoinFamilyResponse,
  AxiosError,
  JoinFamilyRequest
> => {
  const { setMode } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<JoinFamilyResponse, AxiosError, JoinFamilyRequest>({
    mutationFn: async (data: JoinFamilyRequest) => {
      const response = await axios.post<JoinFamilyResponse>(
        '/api/users/join-family',
        data,
      );
      return response.data;
    },
    onSuccess: async (data, request) => {
      setMode(AuthMode.LOGIN);
      updateLocalStorageUserID(request.user_id);
      await FirebaseAuthentication.signInWithCustomToken({
        token: data.custom_token,
      });
      await queryClient.refetchQueries('user');
      queryClient.clear();
      navigate('/');
    },
  });
};

export interface CheckFamilyCodeRequest {
  join_code: string;
}

export interface FamilyMember {
  id: number;
  uuid: string;
  firstname: string;
  name?: string;
  user_type: string;
}

export interface CheckFamilyCodeResponse {
  exists: boolean;
  name?: string; // May include additional info about the family
  id?: number; // ID of the family
  members?: FamilyMember[]; // List of family members
}

/**
 * Mutation hook to check if a family code is valid and get family members (anonymous endpoint)
 * @returns A mutation result object with methods to check a family code
 */
export const useCheckFamilyCode = (): UseMutationResult<
  CheckFamilyCodeResponse,
  AxiosError,
  CheckFamilyCodeRequest
> => {
  return useMutation<
    CheckFamilyCodeResponse,
    AxiosError,
    CheckFamilyCodeRequest
  >({
    mutationFn: async (data: CheckFamilyCodeRequest) => {
      const response = await axios.post<CheckFamilyCodeResponse>(
        '/api/users/family-code',
        data,
      );
      return response.data;
    },
  });
};
